import { normalizeInviteesResponse } from './normalize_invitees_response'

const isValidEmail = (email) => {
  if (typeof email !== 'string') return false
  const trimmed = email.trim()
  return trimmed.length > 0 && /@/.test(trimmed)
}

const collectValidEmails = (items, { excludeEmail } = {}) => {
  const excluded = typeof excludeEmail === 'string' ? excludeEmail.trim().toLowerCase() : null
  const seen = new Set()
  const recipients = [];

  (items || []).forEach((item) => {
    const email = item?.email
    if (!isValidEmail(email)) return

    const normalized = email.trim().toLowerCase()
    if (seen.has(normalized)) return
    if (excluded && normalized === excluded) return

    seen.add(normalized)
    recipients.push(email.trim())
  })

  return recipients
}

/**
 * Builds the deduped bulk-email recipient list for an external meeting's
 * attendees, defensively excluding the logged-in user's own email even
 * though callers should already exclude it via getExternalAttendees.
 */
export const collectExternalBulkEmailRecipients = (attendees, currentUserEmail) => collectValidEmails(
  attendees,
  { excludeEmail: currentUserEmail }
)

/**
 * Builds the ordered attendee list for an external (non-Calendly) meeting.
 * Mirrors what ExternalInviteesContent renders: the meeting's invitees, plus
 * event memberships (e.g. the host) when the viewer isn't the meeting owner.
 */
export const getExternalAttendees = (event, currentUserUuid) => {
  const { invitees, eventMemberships, ownerUserUuid } = event
  const attendees = invitees || []

  if (ownerUserUuid !== currentUserUuid) {
    return [...(eventMemberships || []), ...attendees]
  }

  return attendees
}

const fetchInviteePages = async ({ trigger, eventId, page, preferCacheValue = false }) => {
  const request = preferCacheValue
    ? trigger({ eventId, page }, true)
    : trigger({ eventId, page })
  const normalized = normalizeInviteesResponse(
    await request.unwrap()
  )

  if (!normalized.pagination.nextPage) {
    return normalized.results
  }

  const remainingInvitees = await fetchInviteePages({
    trigger,
    eventId,
    page: normalized.pagination.currentPage + 1,
  })

  return [...normalized.results, ...remainingInvitees]
}

export const fetchAllInviteesForBulkEmail = async ({ trigger, eventId }) => {
  const invitees = await fetchInviteePages({
    trigger,
    eventId,
    page: 1,
    preferCacheValue: true,
  })

  return collectValidEmails(invitees)
}
